import { Button, Form, Input, Modal, Text } from '@opengov/component-library/dist';
import { IReportLocale } from '@opengov/reporting-app/application/common/locale';
import { IReport } from '@opengov/reporting-types';
import React, { useEffect, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';

import { createReportConfigurationTemplate } from './createReportConfigurationTemplate';
import { fetchDataForClipboard } from './fetchDataForClipboard';

import styles from './StandardTemplateModal.scss';

export interface IStandardTemplateModalProps {
  currentReport: IReport;
  reportLocaleText: IReportLocale;
  onCopy?(reportConfigurationTemplateString: string): void;
  closeModal(): void;
}

export const StandardTemplateModal: React.FC<IStandardTemplateModalProps> = (props) => {
  const { closeModal, reportLocaleText, currentReport, onCopy = () => {} } = props;
  const [templateName, setTemplateName] = useState(currentReport.title);
  const [isCopiedToClipboard, setIsCopiedToClipboard] = useState(false);
  const [isCopyDisabled, setIsCopyDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reportConfigurationTemplateData, setReportConfigurationTemplateData] = useState(null);
  const title = reportLocaleText.standardTemplate.modalTitle;
  const description = reportLocaleText.standardTemplate.modalDescription;

  // WARNING: We don't typically fetch data like this in components but doing so here because of the way copy to clipboard works.
  const fetchData = async () => {
    try {
      setIsLoading(true);

      const { savedViewsWithReportConfigurations, lens, defaultReportConfiguration } = await fetchDataForClipboard(
        currentReport.id,
        currentReport.lensId,
        currentReport.defaultReportConfiguration
      );

      setReportConfigurationTemplateData({
        defaultReportConfiguration,
        standardReportName: currentReport.title,
        report: currentReport,
        templatedSavedViews: savedViewsWithReportConfigurations,
        lens
      });

      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
      setIsCopyDisabled(true);
    }
  };

  const getStringifiedTemplateData = () => {
    return JSON.stringify(createReportConfigurationTemplate(reportConfigurationTemplateData));
  };

  const handleCopy = () => {
    setIsCopiedToClipboard(true);
    onCopy(getStringifiedTemplateData());
  };

  const handleOnChange = (value) => {
    setReportConfigurationTemplateData({
      ...reportConfigurationTemplateData,
      standardReportName: value
    });

    setTemplateName(value);
  };

  useEffect(() => {
    void fetchData();
  }, []);

  return (
    <Modal.Work onClose={closeModal} title={title} description={description}>
      <div className={styles.modalContentWrapper}>
        <Form.Item label="Template Name" htmlFor="standardTemplateNameInput" dataTest="template-input">
          <Input.Text
            id="standardTemplateNameInput"
            value={templateName}
            onChange={handleOnChange}
            placeholder="Enter Template Name"
            dataTest="template-name-input"
          />
        </Form.Item>
        <div className={styles.modalActions}>
          <Button size="default" variant="neutral" onClick={closeModal} dataTest="standard-template-close-modal-button">
            {reportLocaleText.standardTemplate.closeModalButton}
          </Button>
          <CopyToClipboard text={getStringifiedTemplateData()} onCopy={handleCopy}>
            <Button
              disabled={isCopyDisabled}
              loading={isLoading}
              size="default"
              variant="primary"
              dataTest="standard-template-copy-to-clipboard-button"
            >
              {reportLocaleText.standardTemplate.copyToClipboardButton}
            </Button>
          </CopyToClipboard>
        </div>
        <div className={styles.copyMessage}>
          <Text size="small" font="condensed" color="light">
            {isCopiedToClipboard && reportLocaleText.standardTemplate.copyTemplateSuccessMessage}
          </Text>
        </div>
      </div>
    </Modal.Work>
  );
};
